/**
 * A janela de desenho do terreno — aritmética pura, sem Phaser (PRD §15.1).
 *
 * As camadas de tilemap deixaram de ter o tamanho da **região** e passaram a ter o tamanho
 * de uma **janela** que rola com a câmera. O motivo está medido em §15.1: cada camada aloca
 * um objeto de tile por célula, e são cinco camadas. Numa região de 512 × 512 × 8 isso são
 * 2,6 milhões de objetos criados no Welcome, contra 30.720 com janela — e o `setFloor`, que
 * redesenha tudo, sai de 262.144 células para 6.144.
 *
 * Este módulo é separado do `Terrain` de propósito: é a parte que erra em silêncio se
 * errar (um deslocamento trocado desenha o mundo inteiro um chunk fora do lugar) e a única
 * que dá para testar sem uma GPU. A regra que o comentário de `terrain.ts` sugere para
 * aritmética de deslocamento — "um único par de funções de conversão" — é este arquivo.
 */

/** Lado do chunk de terreno, em tiles. A janela é alinhada a ele. */
export const CHUNK_TILES = 16;

/**
 * Tamanho da janela num eixo: a viewport arredondada para cima em chunk, mais **um chunk de
 * folga de cada lado**, limitada pelo tamanho da região.
 *
 * A folga existe para a janela não precisar rolar a cada passo: sem ela, o tile que entra
 * na tela seria sempre o tile da borda da janela, e todo passo pediria um redesenho. Com um
 * chunk de cada lado, o jogador anda 16 tiles entre uma rolagem e a seguinte.
 *
 * A limitação pela região é o que faz este bloco **não** custar nada nas duas regiões de
 * hoje: a Vila do Norte tem 80 × 64 e a janela nunca passa disso, então ela continua sendo
 * a região inteira e o custo é exatamente o de antes.
 */
export function windowSize(viewTiles: number, regionTiles: number): number {
  const chunks = Math.ceil(Math.max(viewTiles, 1) / CHUNK_TILES) + 2;
  return Math.min(regionTiles, chunks * CHUNK_TILES);
}

/**
 * Origem da janela que centraliza `center`, alinhada a chunk e presa dentro da região.
 *
 * O alinhamento não é cosmético: uma janela que começa em múltiplo de 16 tem borda
 * coincidente com a borda de chunk, e o chunk que chega ou está inteiro dentro ou inteiro
 * fora dela. A prisão à região vem depois do alinhamento e pode desalinhá-la — na borda do
 * mapa isso é o certo, porque ali não há para onde rolar.
 */
export function windowOrigin(center: number, size: number, regionTiles: number): number {
  const raw = center - Math.floor(size / 2);
  const aligned = Math.floor(raw / CHUNK_TILES) * CHUNK_TILES;
  return Math.max(0, Math.min(regionTiles - size, aligned));
}

/**
 * A janela precisa rolar para acompanhar `center`?
 *
 * A histerese é o que evita o redesenho por passo: enquanto o jogador estiver a mais de um
 * chunk de qualquer borda da janela, ela fica onde está. Só quando ele entra nessa faixa é
 * que a origem é recalculada — e como `windowOrigin` o recoloca no meio, ele volta a estar
 * a meia janela da borda.
 *
 * Na borda do mapa a origem recalculada é igual à atual (a prisão à região a segura), e o
 * chamador não redesenha nada. É por isso que a resposta é a origem nova e não um booleano:
 * quem compara é quem sabe se algo mudou.
 */
export function scrolledOrigin(
  center: number,
  origin: number,
  size: number,
  regionTiles: number,
): number {
  const margin = CHUNK_TILES;

  if (center >= origin + margin && center < origin + size - margin) return origin;
  return windowOrigin(center, size, regionTiles);
}
